import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';

// API Configuration - Replace with your actual API keys
const API_KEYS = {
  FRED: '6dcfaa0581302389773b5af1fee04948',
  ALPHA_VANTAGE: 'KTPZK4X52UV7Y266',
  // CoinGecko doesn't require an API key
};

// FRED Series IDs for macro indicators
const FRED_SERIES = {
  GDP: 'GDP',
  UNEMPLOYMENT: 'UNRATE',
  INFLATION: 'CPIAUCSL',
  FED_FUNDS: 'FEDFUNDS',
  CONSUMER_CONFIDENCE: 'UMCSENT',
  TREASURY_10Y: 'DGS10',
};

// Mock data fallback for when APIs fail
const getMockMacroData = () => ({
  gdp: { value: '2.8', change: '0.3', trend: 'up' as const },
  unemployment: { value: '3.7', change: '-0.1', trend: 'down' as const },
  inflation: { value: '3.2', change: '0.2', trend: 'up' as const },
  fedFunds: { value: '5.33', change: '0.0', trend: 'neutral' as const },
  consumerConf: { value: '102.6', change: '1.2', trend: 'up' as const },
  treasury10y: { value: '4.25', change: '0.05', trend: 'up' as const },
});

const getMockMarketData = () => ({
  stocks: [
    { symbol: 'S&P 500 (SPY)', value: '4783.45', change: '+0.85%', trend: 'up' as const },
    { symbol: 'DOW JONES (DIA)', value: '37440.34', change: '+0.52%', trend: 'up' as const },
    { symbol: 'NASDAQ (QQQ)', value: '14963.87', change: '+1.23%', trend: 'up' as const },
    { symbol: 'RUSSELL 2000 (IWM)', value: '2027.56', change: '+0.67%', trend: 'up' as const }
  ],
  forex: [
    { symbol: 'EUR/USD (FXE)', value: '1.0845', change: '+0.12%', trend: 'up' as const },
    { symbol: 'GBP/USD (FXB)', value: '1.2678', change: '-0.08%', trend: 'down' as const },
    { symbol: 'USD/JPY (FXY)', value: '148.32', change: '+0.25%', trend: 'up' as const },
    { symbol: 'AUD/USD (FXA)', value: '0.6589', change: '+0.15%', trend: 'up' as const }
  ],
  crypto: [
    { symbol: 'BTC/USD (BTCUSDT)', value: '43250.00', change: '+2.45%', trend: 'up' as const },
    { symbol: 'ETH/USD (ETHUSDT)', value: '2280.50', change: '+1.85%', trend: 'up' as const },
    { symbol: 'BNB/USD (BNBUSDT)', value: '315.20', change: '+0.95%', trend: 'up' as const },
    { symbol: 'SOL/USD (SOLUSDT)', value: '98.75', change: '+3.20%', trend: 'up' as const }
  ],
  commodities: [
    { symbol: 'GOLD (GLD)', value: '2045.30', change: '+0.45%', trend: 'up' as const },
    { symbol: 'SILVER (SLV)', value: '23.85', change: '+0.32%', trend: 'up' as const },
    { symbol: 'CRUDE OIL (USO)', value: '73.25', change: '-0.55%', trend: 'down' as const },
    { symbol: 'NATURAL GAS (UNG)', value: '2.68', change: '+1.20%', trend: 'up' as const }
  ]
});

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Home() {
  const [activeMarket, setActiveMarket] = useState('stocks');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [htmlContent, setHtmlContent] = useState('');
  const [grokQuery, setGrokQuery] = useState('');
  const [grokResponse, setGrokResponse] = useState('');
  const [isGrokLoading, setIsGrokLoading] = useState(false);
  const [grokError, setGrokError] = useState('');
  const [macroData, setMacroData] = useState([
    { label: 'GDP Growth', value: 'Loading...', change: '...', trend: 'neutral' },
    { label: 'Unemployment', value: 'Loading...', change: '...', trend: 'neutral' },
    { label: 'Inflation Rate', value: 'Loading...', change: '...', trend: 'neutral' },
    { label: 'Fed Funds Rate', value: 'Loading...', change: '...', trend: 'neutral' },
    { label: 'Consumer Confidence', value: 'Loading...', change: '...', trend: 'neutral' },
    { label: '10Y Treasury', value: 'Loading...', change: '...', trend: 'neutral' }
  ]);
  const [marketData, setMarketData] = useState({
    stocks: [
      { symbol: 'S&P 500 (SPY)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'DOW JONES (DIA)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'NASDAQ (QQQ)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'RUSSELL 2000 (IWM)', value: 'Loading...', change: '...', trend: 'neutral' }
    ],
    forex: [
      { symbol: 'EUR/USD (FXE)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'GBP/USD (FXB)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'USD/JPY (FXY)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'AUD/USD (FXA)', value: 'Loading...', change: '...', trend: 'neutral' }
    ],
    crypto: [
      { symbol: 'BTC/USD (BTCUSDT)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'ETH/USD (ETHUSDT)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'BNB/USD (BNBUSDT)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'SOL/USD (SOLUSDT)', value: 'Loading...', change: '...', trend: 'neutral' }
    ],
    commodities: [
      { symbol: 'GOLD (GLD)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'SILVER (SLV)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'CRUDE OIL (USO)', value: 'Loading...', change: '...', trend: 'neutral' },
      { symbol: 'NATURAL GAS (UNG)', value: 'Loading...', change: '...', trend: 'neutral' }
    ]
  });
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'live' | 'demo'>('demo');
  const [isUploadingCalendar, setIsUploadingCalendar] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [isLoadingPolymarket, setIsLoadingPolymarket] = useState(false);
  const [polymarketError, setPolymarketError] = useState('');
  const [isUploadingPredictions, setIsUploadingPredictions] = useState(false);
  const [predictionsUploadMessage, setPredictionsUploadMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [topNews, setTopNews] = useState<any[]>([]);
  const [latestNews, setLatestNews] = useState<any[]>([]);
  const [isLoadingNews, setIsLoadingNews] = useState(false);
  const [showCalculatorDropdown, setShowCalculatorDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileCalculators, setShowMobileCalculators] = useState(false);

  // Sports Schedule Data
  const sportsSchedule = [
    { date: 'Nov 22', time: '7:00 PM', sport: 'NBA', teams: 'Lakers vs Warriors', channel: 'ESPN' },
    { date: 'Nov 22', time: '8:30 PM', sport: 'NBA', teams: 'Celtics vs Heat', channel: 'TNT' },
    { date: 'Nov 23', time: '1:00 PM', sport: 'NFL', teams: 'Chiefs vs Eagles', channel: 'FOX' },
    { date: 'Nov 23', time: '4:25 PM', sport: 'NFL', teams: 'Cowboys vs 49ers', channel: 'CBS' },
    { date: 'Nov 24', time: '12:30 PM', sport: 'NFL', teams: 'Lions vs Packers', channel: 'CBS' },
    { date: 'Nov 24', time: '4:30 PM', sport: 'NFL', teams: 'Dolphins vs Jets', channel: 'FOX' },
    { date: 'Nov 24', time: '8:20 PM', sport: 'NFL', teams: 'Ravens vs Chargers', channel: 'NBC' },
    { date: 'Nov 25', time: '7:30 PM', sport: 'NBA', teams: 'Bucks vs Nets', channel: 'ESPN' }
  ];

  // Predictions Market Data
  const [predictionMarkets, setPredictionMarkets] = useState([
    { 
      category: 'Sports', 
      event: '2024 NBA Championship Winner', 
      favorite: 'Boston Celtics', 
      odds: '35%', 
      volume: '$2.4M',
      trend: 'up'
    },
    { 
      category: 'Sports', 
      event: 'Super Bowl LIX Winner', 
      favorite: 'Kansas City Chiefs', 
      odds: '28%', 
      volume: '$3.8M',
      trend: 'up'
    },
    { 
      category: 'Politics', 
      event: '2024 Presidential Election', 
      favorite: 'TBD', 
      odds: '52%', 
      volume: '$12.5M',
      trend: 'neutral'
    },
    { 
      category: 'Sports', 
      event: 'UEFA Champions League Winner', 
      favorite: 'Manchester City', 
      odds: '32%', 
      volume: '$1.9M',
      trend: 'down'
    },
    { 
      category: 'Finance', 
      event: 'Bitcoin Above $50K by Dec 31', 
      favorite: 'Yes', 
      odds: '68%', 
      volume: '$5.2M',
      trend: 'up'
    },
    { 
      category: 'Sports', 
      event: 'World Series 2024 Winner', 
      favorite: 'LA Dodgers', 
      odds: '42%', 
      volume: '$1.6M',
      trend: 'neutral'
    }
  ]);

  // Load predictions from Supabase database
  const loadPredictionsFromDB = async () => {
    try {
      const { data, error } = await supabase
        .from('predictions_market')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedData = data.map((item: any) => ({
          category: item.category,
          event: item.event,
          favorite: item.favorite,
          odds: item.yes_odds || item.odds, // Prioritize yes_odds field
          volume: item.volume,
          trend: item.trend
        }));
        setPredictionMarkets(formattedData);
        setPolymarketError('');
      }
    } catch (error) {
      console.error('Error loading predictions from database:', error);
    }
  };

  // Load Polymarket data from Edge Function
  const loadPolymarketData = async () => {
    setIsLoadingPolymarket(true);
    setPolymarketError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/polymarket-data`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch Polymarket data: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.markets && result.markets.length > 0) {
        setPredictionMarkets(result.markets);
        setPolymarketError('');
      } else {
        setPolymarketError('No Polymarket data available');
      }
    } catch (error) {
      console.error('Error loading Polymarket data:', error);
      setPolymarketError('Failed to load Polymarket data');
    } finally {
      setIsLoadingPolymarket(false);
    }
  };

  // Handle Excel/CSV file upload for predictions
  const handlePredictionsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPredictions(true);
    setPredictionsUploadMessage(null);

    try {
      const text = await file.text();
      let predictions: any[] = [];

      // Parse CSV
      if (file.name.endsWith('.csv')) {
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          predictions.push(row);
        }
      } 
      // Parse JSON (Excel exported as JSON)
      else if (file.name.endsWith('.json')) {
        predictions = JSON.parse(text);
      } 
      else {
        throw new Error('Please upload a CSV or JSON file');
      }

      if (!Array.isArray(predictions) || predictions.length === 0) {
        throw new Error('File must contain prediction data');
      }

      // Send to Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-predictions-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            predictions,
            clearExisting: true
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setPredictionsUploadMessage({ type: 'success', text: `Successfully uploaded ${result.count} predictions!` });
        await loadPredictionsFromDB();
        setTimeout(() => setPredictionsUploadMessage(null), 5000);
      } else {
        throw new Error(result.error || 'Upload failed');
      }

    } catch (error) {
      console.error('Error uploading predictions:', error);
      setPredictionsUploadMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to upload predictions data' 
      });
      setTimeout(() => setPredictionsUploadMessage(null), 5000);
    } finally {
      setIsUploadingPredictions(false);
      e.target.value = '';
    }
  };

  // Load all data from Edge Function
  const loadAllData = async () => {
    setIsLoading(true);
    
    try {
      // Fetch all market data from Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-market-data`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch market data: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('=== FULL API RESPONSE ===');
      console.log(JSON.stringify(result, null, 2));

      // FIXED: Check for success flag AND data object
      if (result && result.success === true && result.data) {
        const { macro, stocks, forex, commodities, vix, crypto } = result.data;
        
        console.log('=== PARSED DATA ===');
        console.log('Macro:', macro);
        console.log('Stocks:', stocks);
        console.log('Stocks.sp500:', stocks?.sp500);
        console.log('Stocks.dow:', stocks?.dow);
        console.log('Stocks.nasdaq:', stocks?.nasdaq);
        console.log('Forex:', forex);
        console.log('Commodities:', commodities);
        console.log('Crypto:', crypto);
        console.log('VIX:', vix);

        // Update macro data
        const newMacroData = [
          { 
            label: 'GDP Growth', 
            value: macro?.gdp?.value || 'N/A', 
            change: macro?.gdp?.change || '0.0%', 
            trend: macro?.gdp?.trend || 'neutral' 
          },
          { 
            label: 'Unemployment', 
            value: macro?.unemployment?.value || 'N/A', 
            change: macro?.unemployment?.change || '0.0%', 
            trend: macro?.unemployment?.trend || 'neutral' 
          },
          { 
            label: 'Inflation Rate', 
            value: macro?.inflation?.value || 'N/A', 
            change: macro?.inflation?.change || '0.0%', 
            trend: macro?.inflation?.trend || 'neutral' 
          },
          { 
            label: 'Fed Funds Rate', 
            value: macro?.fedFunds?.value || 'N/A', 
            change: macro?.fedFunds?.change || '0.0%', 
            trend: macro?.fedFunds?.trend || 'neutral' 
          },
          { 
            label: 'Consumer Confidence', 
            value: macro?.consumerConf?.value || 'N/A', 
            change: macro?.consumerConf?.change || '0.0', 
            trend: macro?.consumerConf?.trend || 'neutral' 
          },
          { 
            label: '10Y Treasury', 
            value: macro?.treasury10y?.value || 'N/A', 
            change: macro?.treasury10y?.change || '0.0%', 
            trend: macro?.treasury10y?.trend || 'neutral' 
          }
        ];
        setMacroData(newMacroData);

        // FIXED: Update market data with better null handling
        const newMarketData = {
          stocks: [
            { 
              symbol: 'S&P 500 (SPY)', 
              value: stocks?.spy?.price || 'N/A', 
              change: stocks?.spy?.changePercent ? `${stocks.spy.changePercent}%` : '0.00%', 
              trend: stocks?.spy?.trend || 'neutral' 
            },
            { 
              symbol: 'DOW JONES (DIA)', 
              value: stocks?.dia?.price || 'N/A', 
              change: stocks?.dia?.changePercent ? `${stocks.dia.changePercent}%` : '0.00%', 
              trend: stocks?.dia?.trend || 'neutral' 
            },
            { 
              symbol: 'NASDAQ (QQQ)', 
              value: stocks?.qqq?.price || 'N/A', 
              change: stocks?.qqq?.changePercent ? `${stocks.qqq.changePercent}%` : '0.00%', 
              trend: stocks?.qqq?.trend || 'neutral' 
            },
            { 
              symbol: 'RUSSELL 2000 (IWM)', 
              value: stocks?.russell?.price || 'N/A',
              change: stocks?.russell?.changePercent ? `${stocks.russell.changePercent}%` : '0.00%', 
              trend: stocks?.russell?.trend || 'neutral' 
            }
          ],
          forex: [
            { 
              symbol: 'EUR/USD (FXE)', 
              value: forex?.eurUsd?.price || 'N/A', 
              change: forex?.eurUsd?.changePercent ? `${forex.eurUsd.changePercent}%` : '0.00%', 
              trend: forex?.eurUsd?.trend || 'neutral' 
            },
            { 
              symbol: 'GBP/USD (FXB)', 
              value: forex?.gbpUsd?.price || 'N/A', 
              change: forex?.gbpUsd?.changePercent ? `${forex.gbpUsd.changePercent}%` : '0.00%', 
              trend: forex?.gbpUsd?.trend || 'neutral' 
            },
            { 
              symbol: 'USD/JPY (FXY)', 
              value: forex?.usdJpy?.price || 'N/A', 
              change: forex?.usdJpy?.changePercent ? `${forex.usdJpy.changePercent}%` : '0.00%', 
              trend: forex?.usdJpy?.trend || 'neutral' 
            },
            { 
              symbol: 'AUD/USD (FXA)', 
              value: forex?.audUsd?.price || 'N/A',
              change: forex?.audUsd?.changePercent ? `${forex.audUsd.changePercent}%` : '0.00%', 
              trend: forex?.audUsd?.trend || 'neutral' 
            }
          ],
          crypto: [
            { 
              symbol: 'BTC/USD (BTCUSDT)', 
              value: crypto?.btc?.price || 'N/A', 
              change: crypto?.btc?.changePercent ? `${crypto.btc.changePercent}%` : '0.00%', 
              trend: crypto?.btc?.trend || 'neutral' 
            },
            { 
              symbol: 'ETH/USD (ETHUSDT)', 
              value: crypto?.eth?.price || 'N/A', 
              change: crypto?.eth?.changePercent ? `${crypto.eth.changePercent}%` : '0.00%', 
              trend: crypto?.eth?.trend || 'neutral' 
            },
            { 
              symbol: 'BNB/USD (BNBUSDT)', 
              value: crypto?.bnb?.price || 'N/A', 
              change: crypto?.bnb?.changePercent ? `${crypto.bnb.changePercent}%` : '0.00%', 
              trend: crypto?.bnb?.trend || 'neutral' 
            },
            { 
              symbol: 'SOL/USD (SOLUSDT)', 
              value: crypto?.sol?.price || 'N/A', 
              change: crypto?.sol?.changePercent ? `${crypto.sol.changePercent}%` : '0.00%', 
              trend: crypto?.sol?.trend || 'neutral' 
            }
          ],
          commodities: [
            { 
              symbol: 'GOLD (GLD)', 
              value: commodities?.gold?.price || 'N/A', 
              change: commodities?.gold?.changePercent ? `${commodities.gold.changePercent}%` : '0.00%', 
              trend: commodities?.gold?.trend || 'neutral' 
            },
            { 
              symbol: 'SILVER (SLV)', 
              value: commodities?.silver?.price || 'N/A', 
              change: commodities?.silver?.changePercent ? `${commodities.silver.changePercent}%` : '0.00%', 
              trend: commodities?.silver?.trend || 'neutral' 
            },
            { 
              symbol: 'CRUDE OIL (USO)', 
              value: commodities?.oil?.price || 'N/A', 
              change: commodities?.oil?.changePercent ? `${commodities.oil.changePercent}%` : '0.00%', 
              trend: commodities?.oil?.trend || 'neutral' 
            },
            { 
              symbol: 'NATURAL GAS (UNG)', 
              value: commodities?.natgas?.price || 'N/A',
              change: commodities?.natgas?.changePercent ? `${commodities.natgas.changePercent}%` : '0.00%', 
              trend: commodities?.natgas?.trend || 'neutral' 
            }
          ]
        };
        
        console.log('=== FINAL MARKET DATA BEING SET ===');
        console.log('Stocks array:', JSON.stringify(newMarketData.stocks, null, 2));
        console.log('Commodities array:', JSON.stringify(newMarketData.commodities, null, 2));
        console.log('Forex array:', JSON.stringify(newMarketData.forex, null, 2));
        console.log('Crypto array:', JSON.stringify(newMarketData.crypto, null, 2));
        
        setMarketData(newMarketData);
        setDataSource('live');
        setLastUpdate(new Date());
      } else {
        console.error('Invalid response format:', result);
        console.error('Response has success:', result?.success);
        console.error('Response has data:', !!result?.data);
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data
      const mockMacro = getMockMacroData();
      const mockMarket = getMockMarketData();
      
      setMacroData([
        { label: 'GDP Growth', value: `${mockMacro.gdp.value}%`, change: `${mockMacro.gdp.change}%`, trend: mockMacro.gdp.trend },
        { label: 'Unemployment', value: `${mockMacro.unemployment.value}%`, change: `${mockMacro.unemployment.change}%`, trend: mockMacro.unemployment.trend },
        { label: 'Inflation Rate', value: `${mockMacro.inflation.value}%`, change: `${mockMacro.inflation.change}%`, trend: mockMacro.inflation.trend },
        { label: 'Fed Funds Rate', value: `${mockMacro.fedFunds.value}%`, change: `${mockMacro.fedFunds.change}%`, trend: mockMacro.fedFunds.trend },
        { label: 'Consumer Confidence', value: mockMacro.consumerConf.value, change: mockMacro.consumerConf.change, trend: mockMacro.consumerConf.trend },
        { label: '10Y Treasury', value: `${mockMacro.treasury10y.value}%`, change: `${mockMacro.treasury10y.change}%`, trend: mockMacro.treasury10y.trend }
      ]);
      setMarketData(mockMarket);
      setDataSource('demo');
    } finally {
      setIsLoading(false);
    }
  };

  // Load only crypto data (faster updates)
  const loadCryptoData = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-market-data`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();

      if (result.success && result.data?.crypto) {
        setMarketData(prev => ({
          ...prev,
          crypto: [
            { 
              symbol: 'BTC/USD (BTCUSDT)', 
              value: result.data.crypto?.btc?.price || prev.crypto[0].value, 
              change: result.data.crypto?.btc?.changePercent ? `${result.data.crypto.btc.changePercent}%` : prev.crypto[0].change, 
              trend: result.data.crypto?.btc?.trend || prev.crypto[0].trend 
            },
            { 
              symbol: 'ETH/USD (ETHUSDT)', 
              value: result.data.crypto?.eth?.price || prev.crypto[1].value, 
              change: result.data.crypto?.eth?.changePercent ? `${result.data.crypto.eth.changePercent}%` : prev.crypto[1].change, 
              trend: result.data.crypto?.eth?.trend || prev.crypto[1].trend 
            },
            { 
              symbol: 'BNB/USD (BNBUSDT)', 
              value: result.data.crypto?.bnb?.price || prev.crypto[2].value, 
              change: result.data.crypto?.bnb?.changePercent ? `${result.data.crypto.bnb.changePercent}%` : prev.crypto[2].change, 
              trend: result.data.crypto?.bnb?.trend || prev.crypto[2].trend 
            },
            { 
              symbol: 'SOL/USD (SOLUSDT)', 
              value: result.data.crypto?.sol?.price || prev.crypto[3].value, 
              change: result.data.crypto?.sol?.changePercent ? `${result.data.crypto.sol.changePercent}%` : prev.crypto[3].change, 
              trend: result.data.crypto?.sol?.trend || prev.crypto[3].trend 
            }
          ]
        }));
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error loading crypto data:', error);
    }
  };

  // Economic Calendar Data - now loaded from Supabase
  const [economicCalendar, setEconomicCalendar] = useState([
    { date: 'Jan 15', day: 'Mon', event: 'Retail Sales', time: '8:30 AM', impact: 'high' },
    { date: 'Jan 16', day: 'Tue', event: 'Housing Starts', time: '8:30 AM', impact: 'medium' },
    { date: 'Jan 17', day: 'Wed', event: 'FOMC Minutes', time: '2:00 PM', impact: 'high' },
    { date: 'Jan 18', day: 'Thu', event: 'Jobless Claims', time: '8:30 AM', impact: 'medium' },
    { date: 'Jan 19', day: 'Fri', event: 'Existing Home Sales', time: '10:00 AM', impact: 'low' },
    { date: 'Jan 22', day: 'Mon', event: 'PMI Flash', time: '9:45 AM', impact: 'medium' },
    { date: 'Jan 23', day: 'Tue', event: 'Consumer Confidence', time: '10:00 AM', impact: 'high' },
    { date: 'Jan 24', day: 'Wed', event: 'Durable Goods', time: '8:30 AM', impact: 'medium' },
    { date: 'Jan 25', day: 'Thu', event: 'GDP Advance', time: '8:30 AM', impact: 'high' },
    { date: 'Jan 26', day: 'Fri', event: 'PCE Price Index', time: '8:30 AM', impact: 'high' },
    { date: 'Jan 29', day: 'Mon', event: 'Pending Home Sales', time: '10:00 AM', impact: 'low' },
    { date: 'Jan 30', day: 'Tue', event: 'Consumer Confidence', time: '10:00 AM', impact: 'medium' },
    { date: 'Jan 31', day: 'Wed', event: 'FOMC Decision', time: '2:00 PM', impact: 'high' },
    { date: 'Feb 1', day: 'Thu', event: 'ISM Manufacturing', time: '10:00 AM', impact: 'high' },
    { date: 'Feb 2', day: 'Fri', event: 'Nonfarm Payrolls', time: '8:30 AM', impact: 'high' }
  ]);

  // Load calendar data from Supabase
  const loadCalendarData = async () => {
    try {
      const { data, error } = await supabase
        .from('economic_calendar')
        .select('*')
        .order('event_date', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedData = data.map((item: any) => {
          // Parse date without timezone conversion
          const dateParts = item.event_date.split('-');
          const eventDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          
          return {
            date: `${monthNames[eventDate.getMonth()]} ${eventDate.getDate()}`,
            day: dayNames[eventDate.getDay()],
            event: item.event_name,
            time: item.event_time,
            impact: item.impact
          };
        });
        setEconomicCalendar(formattedData);
      }
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  // Load analysis HTML from Supabase
  const loadAnalysisData = async () => {
    try {
      const { data, error } = await supabase
        .from('market_analysis')
        .select('*')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data && data.html_content) {
        setHtmlContent(data.html_content);
      }
    } catch (error) {
      console.error('Error loading analysis data:', error);
    }
  };

  // Handle JSON file upload
  const handleCalendarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingCalendar(true);
    setUploadMessage(null);

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      // Validate that it's an array
      if (!Array.isArray(jsonData)) {
        throw new Error('JSON file must contain an array of events');
      }

      // Transform JSON data to match database schema
      const events = jsonData.map((row: any) => {
        // Parse date - handle various formats
        let eventDate;
        if (row.date || row.Date || row.DATE || row.event_date) {
          const dateStr = row.date || row.Date || row.DATE || row.event_date;
          eventDate = new Date(dateStr);
        } else {
          eventDate = new Date();
        }

        return {
          event_date: eventDate.toISOString().split('T')[0],
          event_day: (row.day || row.Day || row.DAY || row.event_day || '').toString(),
          event_name: (row.event || row.Event || row.EVENT || row.event_name || '').toString(),
          event_time: (row.time || row.Time || row.TIME || row.event_time || '').toString(),
          impact: (row.impact || row.Impact || row.IMPACT || 'medium').toString().toLowerCase()
        };
      });

      // Send to Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-calendar-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            events,
            clearExisting: true
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setUploadMessage({ type: 'success', text: `Successfully uploaded ${result.count} events!` });
        await loadCalendarData();
        setTimeout(() => setUploadMessage(null), 5000);
      } else {
        throw new Error(result.error || 'Upload failed');
      }

    } catch (error) {
      console.error('Error uploading calendar:', error);
      setUploadMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to upload calendar data' 
      });
      setTimeout(() => setUploadMessage(null), 5000);
    } finally {
      setIsUploadingCalendar(false);
      e.target.value = '';
    }
  };

  // Load news data from Edge Function
  const loadNewsData = async () => {
    setIsLoadingNews(true);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-news-data`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch news: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.topStories && result.latestNews) {
        setTopNews(result.topStories);
        setLatestNews(result.latestNews);
      } else {
        console.error('No news data available');
      }
    } catch (error) {
      console.error('Error loading news:', error);
      // Keep existing mock data on error
    } finally {
      setIsLoadingNews(false);
    }
  };

  // Load data on mount and set up refresh interval
  useEffect(() => {
    loadAllData();
    loadCalendarData();
    loadAnalysisData();
    loadPolymarketData();
    loadPredictionsFromDB();
    loadNewsData();
    
    // Load Google Custom Search script
    const script = document.createElement('script');
    script.src = 'https://cse.google.com/cse.js?cx=57fec377c37f14fcf';
    script.async = true;
    document.body.appendChild(script);
    
    // Refresh all data every 3 minutes (180 seconds)
    const allDataInterval = setInterval(() => {
      loadAllData();
      loadCalendarData();
      loadAnalysisData();
      loadPolymarketData();
      loadPredictionsFromDB();
      loadNewsData();
    }, 3 * 60 * 1000);

    // Refresh crypto data every 30 seconds
    const cryptoInterval = setInterval(() => {
      loadCryptoData();
    }, 30 * 1000);

    return () => {
      clearInterval(allDataInterval);
      clearInterval(cryptoInterval);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Top News with images
  const filteredNews = selectedCategory === 'all' 
    ? latestNews 
    : latestNews.filter(news => news.category === selectedCategory);

  const handleHtmlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/html') {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setHtmlContent(event.target.result as string);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearContent = () => {
    setHtmlContent('');
  };

  const formatUpdateTime = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} minute${diff > 1 ? 's' : ''} ago`;
    const hours = Math.floor(diff / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };

  // Handle Grok AI Search
  const handleGrokSearch = async () => {
    if (!grokQuery.trim()) return;

    setIsGrokLoading(true);
    setGrokError('');
    setGrokResponse('');
    console.log('=== GROK SEARCH STARTED ===');
    console.log('Query:', grokQuery);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/grok-search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ query: grokQuery })
        }
      );

      const result = await response.json();
      console.log('=== GROK SEARCH RESULT ===');
      console.log('Success:', result.success);
      console.log('Response:', result.response);

      if (result.success) {
        setGrokResponse(result.response);
      } else {
        setGrokError(result.error || 'Failed to get response from Grok AI');
      }
    } catch (error) {
      console.error('Grok search error:', error);
      setGrokError('Network error. Please try again.');
    } finally {
      setIsGrokLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Navigation */}
      <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-12">
              <h1 className="text-3xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>FinanceHub</h1>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                <a href="#" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Home</a>
                <div 
                  className="relative"
                  onMouseEnter={() => setShowCalculatorDropdown(true)}
                  onMouseLeave={() => setShowCalculatorDropdown(false)}
                >
                  <button className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer flex items-center gap-1">
                    Calculators
                    <i className={`ri-arrow-down-s-line text-lg transition-transform ${showCalculatorDropdown ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showCalculatorDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2">
                      <a href="#loan-calculator" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        Loan Calculator
                      </a>
                      <a href="#options-calculator" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        Options Calculator
                      </a>
                      <a href="#ai-price-prediction" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        AI Price Prediction Calculator
                      </a>
                    </div>
                  )}
                </div>
                <a href="#news" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">News</a>
                <a href="#real-estate" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Real Estate Data</a>
                <a href="#market-scanner" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Market Scanner</a>
                <Link to="/full-analysis" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Full Analysis</Link>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowSubscribeModal(true)}
                className="hidden md:block px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Subscribe
              </button>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
              <div className="flex flex-col gap-2">
                <a 
                  href="#" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Home
                </a>
                
                {/* Mobile Calculators Dropdown */}
                <div>
                  <button
                    onClick={() => setShowMobileCalculators(!showMobileCalculators)}
                    className="w-full px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer flex items-center justify-between"
                  >
                    <span>Calculators</span>
                    <i className={`ri-arrow-down-s-line text-lg transition-transform ${showMobileCalculators ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showMobileCalculators && (
                    <div className="ml-4 mt-2 space-y-2">
                      <a 
                        href="#loan-calculator" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Loan Calculator
                      </a>
                      <a 
                        href="#options-calculator" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Options Calculator
                      </a>
                      <a 
                        href="#ai-price-prediction" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        AI Price Prediction Calculator
                      </a>
                    </div>
                  )}
                </div>
                
                <a 
                  href="#news" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  News
                </a>
                <a 
                  href="#real-estate" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Real Estate Data
                </a>
                <a 
                  href="#market-scanner" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Market Scanner
                </a>
                <Link 
                  to="/full-analysis" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Full Analysis
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl max-w-lg w-full border border-gray-700 shadow-2xl">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <i className="ri-vip-crown-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Premium Subscription</h2>
                    <p className="text-sm text-gray-400">Unlock advanced market insights</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSubscribeModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Feature 1 */}
              <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-xl text-green-400"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Technical Analysis Charts</h3>
                  <p className="text-xs text-gray-400">Detailed charts with price targets and comprehensive risk strategies to guide your investment decisions</p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-bank-line text-xl text-blue-400"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Federal Reserve Insights</h3>
                  <p className="text-xs text-gray-400">Latest FED information, policy updates, and expert predictions on monetary policy direction</p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-stack-line text-xl text-purple-400"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Multi-Layer Market Data</h3>
                  <p className="text-xs text-gray-400">Second and third layer financial market data charts for deeper market structure analysis</p>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-xl text-orange-400"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Corporate & Real Estate Analysis</h3>
                  <p className="text-xs text-gray-400">In-depth data analysis on largest companies and real estate markets for risk management outlooks</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-800">
              <button 
                onClick={() => setShowSubscribeModal(false)}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
              <p className="text-xs text-gray-500 text-center mt-3">Cancel anytime. No commitment required.</p>
            </div>
          </div>
        </div>
      )}

      {/* Market Ticker */}
      <div className="bg-gray-900 py-4 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold text-xs md:text-sm">LIVE MARKET DATA</h3>
            <div className="flex flex-wrap gap-2 justify-end">
              {Object.keys(marketData).map((market) => (
                <button
                  key={market}
                  onClick={() => setActiveMarket(market)}
                  className={`px-2 md:px-4 py-1 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeMarket === market 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {market.charAt(0).toUpperCase() + market.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            {marketData[activeMarket as keyof typeof marketData].map((item, index) => (
              <div key={index} className="bg-gray-800 p-2 md:p-4 rounded-lg border border-gray-700">
                <div className="text-gray-400 text-[10px] md:text-xs mb-1 truncate">{item.symbol}</div>
                <div className="flex items-center justify-between gap-1">
                  <div className="text-white text-sm md:text-xl font-bold truncate">{item.value}</div>
                  <div className={`flex items-center gap-0.5 md:gap-1 text-[10px] md:text-sm font-semibold whitespace-nowrap ${
                    item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {item.trend === 'up' && <i className="ri-arrow-up-line text-xs md:text-base"></i>}
                    {item.trend === 'down' && <i className="ri-arrow-down-line text-xs md:text-base"></i>}
                    {item.trend === 'neutral' && <i className="ri-subtract-line text-xs md:text-base"></i>}
                    <span className="text-[10px] md:text-sm">{item.change}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrolling Ticker Bar */}
      <div className="bg-gray-900 py-3 border-b border-gray-800 overflow-hidden">
        <div className="flex animate-scroll whitespace-nowrap">
          {/* Stocks */}
          {marketData.stocks.map((item, index) => (
            <div key={`stocks-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {/* Forex */}
          {marketData.forex.map((item, index) => (
            <div key={`forex-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {/* Crypto */}
          {marketData.crypto.map((item, index) => (
            <div key={`crypto-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {/* Commodities */}
          {marketData.commodities.map((item, index) => (
            <div key={`commodities-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {/* Duplicate for seamless loop */}
          {marketData.stocks.map((item, index) => (
            <div key={`stocks-dup-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {marketData.forex.map((item, index) => (
            <div key={`forex-dup-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {marketData.crypto.map((item, index) => (
            <div key={`crypto-dup-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
          {marketData.commodities.map((item, index) => (
            <div key={`commodities-dup-${index}`} className="inline-flex items-center gap-2 px-6 border-r border-gray-800">
              <span className="text-gray-400 text-sm font-medium">{item.symbol}</span>
              <span className="text-white text-sm font-bold">{item.value}</span>
              <span className={`text-sm font-semibold ${
                item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {item.change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Search Bar Section */}
      <div className="bg-gray-900 py-4 border-b border-gray-800 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Google Custom Search */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-google-fill text-lg text-white"></i>
                <h3 className="text-sm font-semibold text-white">Google Search</h3>
              </div>
              <div className="gcse-search"></div>
            </div>

            {/* Grok AI Search */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-robot-2-line text-lg text-blue-400"></i>
                <h3 className="text-sm font-semibold text-white">Grok AI Search</h3>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={grokQuery}
                  onChange={(e) => setGrokQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && grokQuery.trim() && !isGrokLoading) {
                      handleGrokSearch();
                    }
                  }}
                  placeholder="Ask Grok AI about markets, news, analysis..."
                  className="flex-1 px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg outline-none text-sm placeholder-gray-500 focus:border-blue-500 transition-colors"
                  disabled={isGrokLoading}
                />
                <button
                  onClick={handleGrokSearch}
                  disabled={isGrokLoading || !grokQuery.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGrokLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Thinking...
                    </>
                  ) : (
                    <>
                      <i className="ri-sparkling-line"></i>
                      Ask Grok
                    </>
                  )}
                </button>
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {['Market trends', 'Stock analysis', 'Crypto news', 'Economic outlook'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setGrokQuery(suggestion);
                      setGrokResponse('');
                      setGrokError('');
                    }}
                    disabled={isGrokLoading}
                    className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded-full hover:bg-gray-700 border border-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>

              {/* Grok Response Display */}
              {(grokResponse || grokError) && (
                <div className="mt-3 p-4 bg-gray-800 border border-gray-700 rounded-lg">
                  {grokError ? (
                    <div className="flex items-start gap-2 text-red-400 text-sm">
                      <i className="ri-error-warning-line text-lg mt-0.5"></i>
                      <span>{grokError}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                          <i className="ri-sparkling-fill"></i>
                          <span>Grok AI Response</span>
                        </div>
                        <button
                          onClick={() => {
                            setGrokResponse('');
                            setGrokError('');
                          }}
                          className="text-gray-500 hover:text-gray-300 transition-colors"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                      <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {grokResponse}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Economic Calendar */}
          <div className="lg:col-span-2">
            {/* Market Analysis Section - Mobile Only - MOVED TO TOP */}
            <div className="lg:hidden bg-gray-900 rounded-xl p-1 border border-gray-800 mb-6">
              <div className="flex items-center justify-between mb-4 px-3">
                <div className="flex items-center gap-2">
                  <i className="ri-bar-chart-box-line text-xl text-blue-500"></i>
                  <h2 className="text-lg font-bold text-white">Market Analysis</h2>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".html"
                      onChange={handleHtmlUpload}
                      className="hidden"
                    />
                    <i className="ri-upload-line text-blue-500 hover:text-blue-400 transition-colors"></i>
                  </label>
                  {htmlContent && (
                    <button onClick={handleClearContent} className="text-red-500 hover:text-red-400 transition-colors">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              </div>
              {htmlContent ? (
                <div className="analysis-content max-h-[700px] overflow-y-auto overflow-x-hidden px-1" dangerouslySetInnerHTML={{ __html: htmlContent }} />
              ) : (
                <div className="text-center py-8 px-3">
                  <i className="ri-file-chart-line text-4xl text-gray-700 mb-3"></i>
                  <p className="text-gray-400 text-sm">No analysis data available</p>
                  <p className="text-xs text-gray-500 mt-2">Upload your Python-generated HTML analysis</p>
                </div>
              )}
            </div>

            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-line text-xl text-blue-500"></i>
                  <h2 className="text-lg font-bold text-white">Economic Calendar</h2>
                </div>
                <label className="cursor-pointer" title="Upload CSV/JSON file">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleCalendarUpload}
                    className="hidden"
                    disabled={isUploadingCalendar}
                  />
                  <i className={`ri-upload-line text-blue-500 hover:text-blue-400 transition-colors ${isUploadingCalendar ? 'opacity-50' : ''}`}></i>
                </label>
              </div>
              
              {uploadMessage && (
                <div className={`mb-4 p-2 rounded text-xs ${
                  uploadMessage.type === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                }`}>
                  {uploadMessage.text}
                </div>
              )}

              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {economicCalendar.map((event, index) => {
                  const impactColor = event.impact === 'high' ? 'border-red-500' : event.impact === 'medium' ? 'border-yellow-500' : 'border-green-500';
                  return (
                    <div key={index} className={`border-l-2 ${impactColor} pl-3`}>
                      <div className="text-xs text-gray-400">{event.date}, {event.day}</div>
                      <div className="text-sm font-semibold text-white mt-1">{event.event}</div>
                      <div className="text-xs text-gray-400 mt-1">{event.time}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Macro Indicators */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">Macro Indicators</h3>
              <div className="space-y-3">
                {macroData.map((indicator, index) => (
                  <div key={index} className="border-b border-gray-800 pb-3 last:border-0 last:pb-0">
                    <span className="text-sm text-gray-400 block mb-1">{indicator.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${
                        indicator.trend === 'up' ? 'text-green-400' : 
                        indicator.trend === 'down' ? 'text-red-400' : 
                        'text-white'
                      }`}>
                        {indicator.value}
                      </span>
                      {indicator.trend !== 'neutral' && (
                        <span className={`text-sm ${
                          indicator.trend === 'up' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {indicator.change}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Predictions Market Overview */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-stock-line text-xl text-purple-500"></i>
                  <h3 className="text-lg font-bold text-white">Predictions Market</h3>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer" title="Upload CSV/JSON file">
                    <input
                      type="file"
                      accept=".csv,.json"
                      onChange={handlePredictionsUpload}
                      className="hidden"
                      disabled={isUploadingPredictions}
                    />
                    <i className={`ri-upload-line text-purple-500 hover:text-purple-400 transition-colors ${isUploadingPredictions ? 'opacity-50' : ''}`}></i>
                  </label>
                  <button
                    onClick={loadPolymarketData}
                    disabled={isLoadingPolymarket}
                    className="text-purple-500 hover:text-purple-400 transition-colors disabled:opacity-50"
                    title="Refresh Polymarket data"
                  >
                    <i className={`ri-refresh-line ${isLoadingPolymarket ? 'animate-spin' : ''}`}></i>
                  </button>
                </div>
              </div>

              {predictionsUploadMessage && (
                <div className={`mb-3 p-2 rounded text-xs ${
                  predictionsUploadMessage.type === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                }`}>
                  {predictionsUploadMessage.text}
                </div>
              )}

              {polymarketError && (
                <div className="mb-3 p-2 bg-red-900/30 text-red-400 text-xs rounded">
                  {polymarketError}
                </div>
              )}

              {isLoadingPolymarket && predictionMarkets.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-loader-4-line text-3xl text-purple-500 animate-spin mb-3"></i>
                  <p className="text-gray-400 text-sm">Loading Polymarket data...</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                  {predictionMarkets.map((market, index) => {
                    const categoryColor = 
                      market.category === 'Sports' ? 'text-green-400' :
                      market.category === 'Politics' ? 'text-blue-400' :
                      'text-orange-400';
                    
                    return (
                      <div key={index} className="bg-gray-800 p-3 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-semibold ${categoryColor}`}>{market.category}</span>
                          <div className="flex items-center gap-1">
                            {market.trend === 'up' && <i className="ri-arrow-up-line text-green-400 text-xs"></i>}
                            {market.trend === 'down' && <i className="ri-arrow-down-line text-red-400 text-xs"></i>}
                            {market.trend === 'neutral' && <i className="ri-subtract-line text-gray-400 text-xs"></i>}
                            <span className="text-xs text-gray-400">{market.volume}</span>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-white mb-1">{market.event}</div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{market.favorite}</span>
                          <span className="text-sm font-bold text-purple-400">{market.odds}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Center Content */}
          <div className="lg:col-span-7">
            {/* Market Analysis Section - Desktop Only - NOW AT TOP */}
            <div id="analysis" className="hidden lg:block bg-gray-900 rounded-xl p-2 border border-gray-800 mb-6">
              <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex items-center gap-2">
                  <i className="ri-bar-chart-box-line text-xl text-blue-500"></i>
                  <h2 className="text-lg font-bold text-white">Market Analysis</h2>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".html"
                      onChange={handleHtmlUpload}
                      className="hidden"
                    />
                    <i className="ri-upload-line text-blue-500 hover:text-blue-400 transition-colors"></i>
                  </label>
                  {htmlContent && (
                    <button onClick={handleClearContent} className="text-red-500 hover:text-red-400 transition-colors">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              </div>
              {htmlContent ? (
                <div className="analysis-content max-h-[1050px] overflow-y-auto px-2" dangerouslySetInnerHTML={{ __html: htmlContent }} />
              ) : (
                <div className="text-center py-8 px-2">
                  <i className="ri-file-chart-line text-4xl text-gray-700 mb-3"></i>
                  <p className="text-gray-400 text-sm">No analysis data available</p>
                  <p className="text-xs text-gray-500 mt-2">Upload your Python-generated HTML analysis</p>
                </div>
              )}
            </div>

            {/* Market Insights */}
            <div id="insights" className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                {
                  id: 1,
                  title: 'Investment Strategies & Market Volatility',
                  description: 'Comprehensive analysis of current market conditions, volatility patterns, and expert insights on portfolio diversification, asset allocation strategies, and risk management techniques for navigating uncertain economic environments.',
                  icon: 'ri-line-chart-line',
                  color: 'bg-blue-100 text-blue-600'
                },
                {
                  id: 2,
                  title: 'Global Economic Outlook & Sector Performance',
                  description: 'In-depth examination of worldwide economic trends, growth forecasts, geopolitical factors influencing international markets, and detailed breakdown of industry sector performance across market segments.',
                  icon: 'ri-global-line',
                  color: 'bg-green-100 text-green-600'
                }
              ].map((insight) => (
                <div key={insight.id} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-blue-500 transition-colors cursor-pointer">
                  <div className={`w-12 h-12 rounded-lg ${insight.color} flex items-center justify-center mb-4`}>
                    <i className={`${insight.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{insight.title}</h3>
                  <p className="text-sm text-gray-400">{insight.description}</p>
                </div>
              ))}
            </div>

            {/* Latest News */}
            <div id="news" className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">Latest Financial News</h2>
              
              {/* Category Filter */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {['all', 'Markets', 'Economy', 'Technology', 'Banking', 'Energy', 'Cryptocurrency'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* News Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredNews.map((article) => (
                  <div key={article.id} className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer">
                    <img src={article.image} alt={article.title} className="w-full h-40 object-cover object-top" />
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-blue-400">{article.category.toUpperCase()}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">{article.time}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-2 line-clamp-2">{article.title}</h3>
                      <p className="text-xs text-gray-400 line-clamp-2">{article.excerpt}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-3">
            {/* Top Stories with Images */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
              <h3 className="text-lg font-bold text-white mb-4">Top Stories</h3>
              <div className="space-y-4">
                {topNews.map((story) => (
                  <div key={story.id} className="border-b border-gray-800 pb-4 last:border-0 last:pb-0 cursor-pointer hover:opacity-80 transition-opacity">
                    <img src={story.image} alt={story.title} className="w-full h-40 object-cover object-top rounded-lg mb-3" />
                    <div className="text-xs text-blue-400 font-semibold mb-1">{story.category.toUpperCase()}</div>
                    <h4 className="text-sm font-semibold text-white mb-2 line-clamp-2">{story.title}</h4>
                    <p className="text-xs text-gray-400 line-clamp-2">{story.excerpt}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">{story.time}</span>
                      <span className="text-xs text-gray-500">{story.source}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-6 border border-blue-700">
              <i className="ri-mail-line text-3xl text-blue-300 mb-3"></i>
              <h3 className="text-lg font-bold text-white mb-2">Stay Updated</h3>
              <p className="text-blue-100 text-sm mb-4">Get daily market insights delivered to your inbox</p>
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-2 bg-blue-950 text-white border border-blue-700 rounded-lg outline-none text-sm placeholder-blue-400 mb-3"
              />
              <button className="w-full px-4 py-2 bg-white text-blue-900 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                Subscribe Now
              </button>
            </div>

            {/* Sports Schedule */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-basketball-line text-xl text-green-500"></i>
                <h3 className="text-lg font-bold text-white">Sports Schedule</h3>
              </div>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {sportsSchedule.map((game, index) => (
                  <div key={index} className="border-l-2 border-green-500 pl-3 hover:bg-gray-800 p-2 rounded-r transition-colors cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-green-400">{game.sport}</span>
                      <span className="text-xs text-gray-500">{game.channel}</span>
                    </div>
                    <div className="text-sm font-semibold text-white">{game.teams}</div>
                    <div className="text-xs text-gray-400 mt-1">{game.date} • {game.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-line-chart-line text-xl text-blue-500"></i>
                <h3 className="text-lg font-bold text-white">FinanceHub</h3>
              </div>
              <p className="text-sm text-gray-400">Your trusted source for real-time financial data and market insights.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Markets</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Stocks</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Forex</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Crypto</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Commodities</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Analysis</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Calendar</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">News</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Education</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Follow Us</h4>
              <div className="flex gap-3">
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-line"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm text-gray-400">
            <p>&copy; 2024 FinanceHub. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 60s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        
        /* Analysis Content Dark Theme Styling */
        .analysis-content {
          color: #e5e7eb;
          line-height: 1.6;
          max-width: 100%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        /* Headings */
        .analysis-content h1 {
          color: #ffffff;
          font-size: 1.875rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          margin-top: 2rem;
          border-bottom: 2px solid #374151;
          padding-bottom: 0.5rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content h2 {
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          margin-top: 1.5rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content h3 {
          color: #f3f4f6;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          margin-top: 1.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content h4 {
          color: #f3f4f6;
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 1rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content h5, .analysis-content h6 {
          color: #d1d5db;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 0.75rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        /* Paragraphs */
        .analysis-content p {
          color: #9ca3af;
          margin-bottom: 1rem;
          line-height: 1.7;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        /* Links */
        .analysis-content a {
          color: #60a5fa;
          text-decoration: underline;
          transition: color 0.2s;
          word-wrap: break-word;
        }
        
        .analysis-content a:hover {
          color: #93c5fd;
        }
        
        /* Lists */
        .analysis-content ul, .analysis-content ol {
          color: #9ca3af;
          margin-bottom: 1rem;
          padding-left: 1.5rem;
          max-width: 100%;
        }
        
        .analysis-content li {
          margin-bottom: 0.5rem;
          word-wrap: break-word;
        }
        
        .analysis-content ul li {
          list-style-type: disc;
        }
        
        .analysis-content ol li {
          list-style-type: decimal;
        }
        
        /* Tables */
        .analysis-content table {
          width: 100%;
          max-width: 100%;
          border-collapse: collapse;
          margin-bottom: 1.5rem;
          background: #1f2937;
          border-radius: 0.5rem;
          overflow: hidden;
          display: block;
          overflow-x: auto;
        }
        
        .analysis-content thead {
          background: #374151;
        }
        
        .analysis-content th {
          color: #ffffff;
          font-weight: 600;
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 2px solid #4b5563;
          white-space: nowrap;
        }
        
        .analysis-content td {
          color: #d1d5db;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #374151;
          word-wrap: break-word;
        }
        
        .analysis-content tbody tr:hover {
          background: #374151;
        }
        
        .analysis-content tbody tr:last-child td {
          border-bottom: none;
        }
        
        /* Code blocks */
        .analysis-content code {
          background: #1f2937;
          color: #60a5fa;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
          word-wrap: break-word;
        }
        
        .analysis-content pre {
          background: #1f2937;
          color: #e5e7eb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1rem;
          border: 1px solid #374151;
          max-width: 100%;
        }
        
        .analysis-content pre code {
          background: transparent;
          padding: 0;
        }
        
        /* Blockquotes */
        .analysis-content blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #d1d5db;
          font-style: italic;
          background: #1f2937;
          padding: 1rem;
          border-radius: 0.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        /* Horizontal rules */
        .analysis-content hr {
          border: none;
          border-top: 1px solid #374151;
          margin: 2rem 0;
        }
        
        /* Images */
        .analysis-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1rem auto;
          border-radius: 0.5rem;
          border: 1px solid #374151;
        }
        
        /* Strong/Bold */
        .analysis-content strong, .analysis-content b {
          color: #ffffff;
          font-weight: 600;
        }
        
        /* Emphasis/Italic */
        .analysis-content em, .analysis-content i {
          color: #d1d5db;
          font-style: italic;
        }
        
        /* Divs and sections */
        .analysis-content div {
          margin-bottom: 0.5rem;
          max-width: 100%;
        }
        
        /* Custom classes for charts/figures */
        .analysis-content .chart-container,
        .analysis-content .figure-container {
          background: #1f2937;
          padding: 1rem;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
          border: 1px solid #374151;
          max-width: 100%;
        }
        
        .analysis-content .chart-title,
        .analysis-content .figure-title {
          color: #ffffff;
          font-weight: 600;
          margin-bottom: 0.75rem;
          font-size: 1rem;
        }
        
        /* Highlight boxes */
        .analysis-content .highlight,
        .analysis-content .info-box {
          background: #1e3a5f;
          border-left: 4px solid #3b82f6;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content .warning {
          background: #3d2817;
          border-left: 4px solid #f59e0b;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content .success {
          background: #1a3d2e;
          border-left: 4px solid #10b981;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .analysis-content .error {
          background: #3d1a1a;
          border-left: 4px solid #ef4444;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
          max-width: 100%;
          word-wrap: break-word;
        }
        
        /* Small text */
        .analysis-content small {
          color: #6b7280;
          font-size: 0.875rem;
        }
        
        /* Subscript and superscript */
        .analysis-content sub, .analysis-content sup {
          color: #9ca3af;
          font-size: 0.75rem;
        }
        
        /* Definition lists */
        .analysis-content dl {
          margin-bottom: 1rem;
        }
        
        .analysis-content dt {
          color: #ffffff;
          font-weight: 600;
          margin-top: 0.5rem;
        }
        
        .analysis-content dd {
          color: #9ca3af;
          margin-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
